import React, { useCallback, useEffect, useMemo, useState } from 'react';
import InputNumberFormat from 'react-number-format';

import { Save as SaveIcon } from '@styled-icons/boxicons-solid/Save';
import { TrashAlt as TrashAltIcon } from '@styled-icons/boxicons-solid/TrashAlt';
import { Close as CloseIcon } from '@styled-icons/evaicons-solid/Close';
import { toast } from 'shared/toast';

import {
  Box,
  HStack,
  Icon,
  IconButton,
  Input as ChakraInput,
  InputGroup as ChakraInputGroup,
  InputRightAddon,
  Text,
} from '@chakra-ui/react';

import AppTable from 'components/AppTable';

import { PriceSpecificity } from './types';

type PriceByGroupEditableTableProps = {
  data: PriceSpecificity[];
  onUpdatePrice: (referenceId: string, total: number) => void;
  onRemove: (referenceId: string) => void;
  pendingPriceReferenceId?: string | null;
  disableInteractions?: boolean;
};

const formatCurrency = (value?: number | null): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value ?? 0);
};

const PriceByGroupEditableTable: React.FC<PriceByGroupEditableTableProps> = ({
  data,
  onUpdatePrice,
  onRemove,
  pendingPriceReferenceId = null,
  disableInteractions = false,
}) => {
  const [editingReferenceId, setEditingReferenceId] = useState<string | null>(
    null,
  );
  const [editingValue, setEditingValue] = useState<number | null>(null);

  const itemsByReferenceId = useMemo(() => {
    return new Map(data.map((item) => [item.referenceId, item]));
  }, [data]);

  useEffect(() => {
    if (pendingPriceReferenceId) {
      setEditingReferenceId(pendingPriceReferenceId);
      setEditingValue(
        itemsByReferenceId.get(pendingPriceReferenceId)?.total ?? null,
      );
    }
  }, [itemsByReferenceId, pendingPriceReferenceId]);

  const handleStartEditingTotal = useCallback(
    (item: PriceSpecificity) => {
      const isBlockedRow =
        disableInteractions && pendingPriceReferenceId !== item.referenceId;

      if (isBlockedRow) {
        return;
      }

      setEditingReferenceId(item.referenceId);
      setEditingValue(item.total);
    },
    [disableInteractions, pendingPriceReferenceId],
  );

  const handleStopEditingTotal = useCallback(
    (item: PriceSpecificity) => {
      const isPendingRow = pendingPriceReferenceId === item.referenceId;
      const hasNoPrice = item.total === null;

      if (isPendingRow && hasNoPrice) {
        return;
      }

      setEditingReferenceId((currentValue) => {
        if (currentValue === item.referenceId) {
          setEditingValue(null);
          return null;
        }

        return currentValue;
      });
    },
    [pendingPriceReferenceId],
  );

  const handleConfirmEditingTotal = useCallback(
    (item: PriceSpecificity) => {
      if (
        editingValue === null ||
        Number.isNaN(editingValue) ||
        editingValue <= 0
      ) {
        toast({
          description: 'O preço por grupo de insumo deve ser maior que zero.',
          status: 'warning',
        });
        return;
      }

      onUpdatePrice(item.referenceId, editingValue);
      handleStopEditingTotal({ ...item, total: editingValue });
    },
    [editingValue, handleStopEditingTotal, onUpdatePrice],
  );

  const handleCancelEditingTotal = useCallback(
    (item: PriceSpecificity) => {
      setEditingValue(item.total);
      handleStopEditingTotal(item);
    },
    [handleStopEditingTotal],
  );

  return (
    <Box width="100%" mt={4}>
      <AppTable
        data={data}
        cols={[
          { field: 'key', description: 'Código', sortable: false },
          { field: 'description', description: 'Grupo', sortable: false },
          {
            field: 'total',
            description: 'Preço',
            isNumeric: true,
            sortable: false,
          },
          {
            field: 'actions',
            description: 'Ações',
            sortable: false,
            isCentered: true,
          },
        ]}
        mapping={(item: PriceSpecificity) => {
          const isEditing = editingReferenceId === item.referenceId;
          const isBlockedRow =
            disableInteractions && pendingPriceReferenceId !== item.referenceId;
          const isDeleteDisabled = disableInteractions;

          return {
            id: item.referenceId,
            key: <Text fontWeight="bold">{item.referenceId || '-'}</Text>,
            description: (
              <Text whiteSpace="pre-wrap" maxW={{ base: '100%', md: '480px' }}>
                {item.description || '-'}
              </Text>
            ),
            total: isEditing ? (
              <ChakraInputGroup>
                <InputNumberFormat
                  type="tel"
                  customInput={ChakraInput}
                  displayType="input"
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  value={editingValue ?? ''}
                  autoFocus
                  onFocus={(e) => e.target.select()}
                  onValueChange={(values) => {
                    if (isBlockedRow) {
                      return;
                    }

                    setEditingValue(values.floatValue ?? null);
                  }}
                  onBlur={() => {
                    const latest = itemsByReferenceId.get(item.referenceId);

                    if (!latest) {
                      return;
                    }

                    handleConfirmEditingTotal(latest);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      const latest = itemsByReferenceId.get(item.referenceId);

                      if (latest) {
                        handleConfirmEditingTotal(latest);
                      }
                    }

                    if (event.key === 'Escape') {
                      const latest = itemsByReferenceId.get(item.referenceId);

                      if (latest) {
                        handleCancelEditingTotal(latest);
                      }
                    }
                  }}
                  isDisabled={isBlockedRow}
                  style={{
                    textAlign: 'right',
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  }}
                />

                <InputRightAddon px={0}>
                  <IconButton
                    height="100%"
                    colorScheme="blue"
                    aria-label="Salvar valor"
                    title="Salvar valor"
                    borderRadius="0"
                    onClick={() => handleConfirmEditingTotal(item)}
                    width="34px"
                  >
                    <Icon w={4} h={4} as={SaveIcon} />
                  </IconButton>

                  <IconButton
                    height="100%"
                    colorScheme="red"
                    aria-label="Cancelar edição"
                    title="Cancelar edição"
                    borderTopLeftRadius="0"
                    borderBottomLeftRadius="0"
                    onClick={() => handleCancelEditingTotal(item)}
                    width="34px"
                  >
                    <Icon w={4} h={4} as={CloseIcon} />
                  </IconButton>
                </InputRightAddon>
              </ChakraInputGroup>
            ) : (
              <Text
                cursor={isBlockedRow ? 'not-allowed' : 'pointer'}
                fontWeight="medium"
                color={isBlockedRow ? 'gray.400' : 'blue.500'}
                _hover={{ color: isBlockedRow ? 'gray.400' : 'blue.600' }}
                onClick={() => handleStartEditingTotal(item)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleStartEditingTotal(item);
                  }
                }}
                tabIndex={0}
                role="button"
              >
                {formatCurrency(item.total)}
              </Text>
            ),
            actions: (
              <HStack justifyContent="center">
                <IconButton
                  aria-label="Remover grupo de insumo"
                  onClick={() => onRemove(item.referenceId)}
                  colorScheme="red"
                  isDisabled={isDeleteDisabled}
                >
                  <Icon as={TrashAltIcon} />
                </IconButton>
              </HStack>
            ),
          };
        }}
        noDataMessage="Nenhum grupo de insumo cadastrado para este preço."
      />
    </Box>
  );
};

export default PriceByGroupEditableTable;
